// D-341 (3): the site refreshes itself when a new version is detected - but never in the middle of work.
// Pure rules (no DOM) so they are unit-tested; the DOM probes live in pwaAutoUpdate.ts.

export const UPDATED_NOTICE_KEY = 'ote_updated_notice';
export const AUTO_UPDATE_AT_KEY = 'ote_auto_update_at';
/** After an automatic refresh, do not auto-refresh again for this long (a stuck service worker must not cause a reload loop). */
export const AUTO_UPDATE_COOLDOWN_MS = 2 * 60_000;
/** A key pressed this recently counts as "typing". */
export const TYPING_WINDOW_MS = 5_000;
/** FIX-AUTO-REFRESH: while the user is busy, re-check this often (and on every page transition). */
export const WAIT_POLL_MS = 15_000;
/** The manual bar shows only after waiting this long for a quiet moment. */
export const MAX_WAIT_MS = 30 * 60_000;
/** Automatic refreshes that did not clear the "new version" state: a stuck service worker. */
export const AUTO_UPDATE_TRIES_KEY = 'ote_auto_update_tries';
export const STUCK_TRIES = 2;

export interface BusyProbe {
  /** An open modal dialog / drawer (aria-modal). */
  overlayOpen: boolean;
  /** Focus is inside a text field that holds text, or a key was pressed in the last TYPING_WINDOW_MS. */
  typing: boolean;
  /** A form with unsaved changes registered a "leave page?" guard (beforeunload). */
  unsavedChanges: boolean;
  /** ANY text field on the page (input / textarea / contenteditable) holds text, focused or not: a reply being written. */
  fieldsWithText: boolean;
}

export function isUserBusy(p: BusyProbe): boolean {
  return p.overlayOpen || p.typing || p.unsavedChanges || p.fieldsWithText;
}

export type UpdateAction = 'none' | 'apply' | 'wait';

/**
 * - no new version -> nothing
 * - the cooldown after a previous automatic refresh still runs -> nothing (the manual prompt takes over)
 * - idle -> apply now
 * - busy -> wait; apply at the next page transition
 */
export function decideUpdate(input: { needRefresh: boolean; busy: boolean; msSinceLastAuto: number | null }): UpdateAction {
  if (!input.needRefresh) return 'none';
  if (input.msSinceLastAuto !== null && input.msSinceLastAuto < AUTO_UPDATE_COOLDOWN_MS) return 'none';
  return input.busy ? 'wait' : 'apply';
}

/** At the next page transition: apply unless a form still holds unsaved changes. */
export function shouldApplyOnNavigation(p: Pick<BusyProbe, 'unsavedChanges'> & Partial<Pick<BusyProbe, 'fieldsWithText'>>): boolean {
  return !p.unsavedChanges && !p.fieldsWithText;
}

/**
 * FIX-AUTO-REFRESH: the manual "new version" bar is a last resort - only after a long wait for a quiet moment
 * (MAX_WAIT_MS) or when automatic refreshes did not take (a stuck service worker). Busy / cooldown never show it.
 */
export function shouldShowManualBar(p: { waitingMs: number; failedTries: number }): boolean {
  return p.failedTries >= STUCK_TRIES || p.waitingMs >= MAX_WAIT_MS;
}
